import { Box, Popover, type PopoverActions, Typography } from '@mui/material';
import React, { type ReactElement, useEffect, useRef } from 'react';

import OverlaySystemBar from '../OverlaySystemBar/OverlaySystemBar';

interface OverlayProps {
    children: React.ReactNode;
    disabled?: boolean;
    triggerItem: React.ReactNode;
    loaded: boolean;
    error?: Error | null;
    onFirstOpen: () => void;
    systemBarTitle?: string;
    systemBarNavigateUrl?: string;
    open?: boolean;
    onClose?: () => void;
}

/**
 * Overlay component.
 *
 * @param {OverlayProps} props - The props for the Overlay component.
 * @returns {JSX.Element} The rendered Overlay component.
 */
function Overlay({
    children,
    disabled,
    triggerItem,
    loaded,
    error,
    onFirstOpen,
    systemBarNavigateUrl,
    open: controlledOpen,
    onClose,
}: OverlayProps) {
    const popoverActionsRef = useRef<PopoverActions | null>(null);
    const [timeout, setTimeoutIntern] = React.useState<NodeJS.Timeout | null>(null);
    const [wasOpen, setWasOpen] = React.useState(false as boolean);
    const popoverRef = React.useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

    useEffect(() =>
        //Update position
        {
            let timer: NodeJS.Timeout | null = null;
            if (open && popoverActionsRef.current) {
                timer = setTimeout(() => {
                    if (popoverActionsRef.current) popoverActionsRef.current.updatePosition();
                }, 100);
            }
            return () => {
                if (timer) clearTimeout(timer);
            };
        }, [open, loaded, popoverActionsRef]);
    useEffect(() => {
        const clearOnEvent = () => {
            if (timeout) {
                clearTimeout(timeout);
                setTimeoutIntern(null);
            }
        };

        window.addEventListener('blur', clearOnEvent);
        document.querySelector('.contentOuter')?.addEventListener('scroll', clearOnEvent);

        return () => {
            window.removeEventListener('blur', clearOnEvent);
            document.querySelector('.contentOuter')?.removeEventListener('scroll', clearOnEvent);
            if (timeout) {
                clearTimeout(timeout);
                setTimeoutIntern(null);
            }
        };
    }, [timeout]);

    React.useEffect(() => {
        if (open && !wasOpen) {
            onFirstOpen();
            setWasOpen(true);
        }
    }, [open, wasOpen, onFirstOpen]);

    useEffect(() => {
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, []);

    const handleDrag = () =>
        //TODO: Rein oder raus?
        {
            // if (e.clientX !== 0 && e.clientY !== 0)
            // { // Verhindert das Zurücksetzen bei Drag-End (wenn clientX und clientY 0 sind)
            //     setPosition({
            //         x: e.clientX,
            //         y: e.clientY,
            //     });
            // }
        };

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (controlledOpen !== undefined) return; // Skip hover behavior for controlled overlay

        // Clear any existing timeout first
        if (timeout) {
            clearTimeout(timeout);
            setTimeoutIntern(null);
        }

        if (!open) {
            const newTimeout = setTimeout(() => {
                setAnchorEl(e.target as HTMLElement);
                setInternalOpen(true);
                if (!wasOpen) {
                    onFirstOpen();
                    setWasOpen(true);
                }
                setTimeoutIntern(null);
            }, 1500);
            setTimeoutIntern(newTimeout);
        }
    };

    const handleMouseLeave = () => {
        if (controlledOpen !== undefined) return; // Skip hover behavior for controlled overlay

        // Clear timeout to prevent opening
        if (timeout) {
            clearTimeout(timeout);
            setTimeoutIntern(null);
        }

        // Close popover if it's open
        if (open) {
            setAnchorEl(null);
            setInternalOpen(false);
        }
    };

    if (disabled) return <>{triggerItem}</>;

    const enhanceChildren = () => {
        return React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child as ReactElement, { onDrag: handleDrag, inDialog: true });
            }
            return child;
        });
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {triggerItem}
            <Popover
                ref={popoverRef}
                action={popoverActionsRef}
                open={open}
                style={{
                    zIndex: 1302,
                }}
                container={() => document.getElementById('dialogContainer')}
                anchorEl={controlledOpen !== undefined ? document.body : anchorEl}
                onClose={() => {
                    if (onClose) {
                        onClose();
                    } else {
                        setAnchorEl(null);
                        setInternalOpen(false);
                    }
                }}
                anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                transformOrigin={{ vertical: 'center', horizontal: 'center' }}
                slotProps={{
                    paper: {
                        onMouseLeave: () => {
                            if (controlledOpen === undefined) {
                                setAnchorEl(null);
                                setInternalOpen(false);
                            }
                        },
                        sx: {
                            maxWidth: { xs: '90%', sm: '80%', md: '50%' },
                            maxHeight: { xs: '80%', sm: '70%', md: '60%' },
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            boxShadow: `
                                0 0 20px rgba(64, 61, 219, 0.3),
                                0 0 40px rgba(64, 61, 219, 0.2),
                                0 8px 32px rgba(0, 0, 0, 0.12)
                            `,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
            >
                <OverlaySystemBar
                    navigateUrl={systemBarNavigateUrl}
                    onClose={() => {
                        if (onClose) {
                            onClose();
                        } else {
                            setAnchorEl(null);
                            setInternalOpen(false);
                        }
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        p: { xs: 2, sm: 3 }, // 16px mobile, 24px desktop
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {enhanceChildren()}
                    {error && (
                        <Box sx={{ p: 1.25 }}>
                            <Typography variant="body1" color={'error'}>
                                {error.message}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Popover>
        </Box>
    );
}

export default Overlay;
